// @ts-check

/**
 * Reviews files one after another. A failure or a stop ends the queue, because
 * the same cause, such as low credits, would stop the next file too.
 * @template T
 * @param {T[]} files
 * @param {(file: T, index: number) => Promise<unknown>} reviewFile Settles when the review of that file ends.
 * @param {() => boolean} isStopped
 * @returns {Promise<{ done: number }>}
 */
async function reviewFilesInTurn(files, reviewFile, isStopped) {
  let done = 0
  for (const [index, file] of files.entries()) {
    if (isStopped()) break
    try {
      await reviewFile(file, index)
    } catch {
      break
    }
    if (!isStopped()) done += 1
  }
  return { done }
}

/**
 * @param {number} done
 * @param {number} total
 */
function reviewsLeftMessage(done, total) {
  return `${done} of ${total} files reviewed. Attach the other files to review them.`
}

/**
 * What the user reads when a review cannot start: the reason when the server gives one.
 * @param {unknown} error
 */
function reviewStartErrorMessage(error) {
  const code = /** @type {{ code?: string } | null} */ (error)?.code
  if (code === "PAYLOAD_TOO_LARGE") {
    return "This file is too large to review: its text is over 10 MB. Split it into smaller files, then try again."
  }
  return "Could not start the review. Please try again."
}

export { reviewFilesInTurn, reviewsLeftMessage, reviewStartErrorMessage }
